import React, { useEffect } from "react"
import { useDispatch } from "react-redux"
import { Dispatch } from "redux"
import styled from "styled-components" // eslint-disable-line no-restricted-imports
import { FormattedMessage } from "react-intl"
import Text from "@kiwicom/orbit-components/lib/Text"
import TextLink from "@kiwicom/orbit-components/lib/TextLink"
import Button from "@kiwicom/orbit-components/lib/Button"
import Separator from "@kiwicom/orbit-components/lib/Separator"
import KiwicomGuaranteeIcon from "@kiwicom/orbit-components/lib/icons/Guarantee"
import AlertIcon from "@kiwicom/orbit-components/lib/icons/Alert"

import { CONTEXT_TYPES } from "../../consts/journey"
import type { ItineraryContext } from "../../types/ItineraryContext"
import type { Guarantee } from "../../types/primitiveTypes"
import { articles, openHelp } from "../../modules/SmartFAQ/services/SmartFAQDuck"
import Stack from "@kiwicom/orbit-components/lib/Stack"
import Price from "../Price/Price"
import mmb2RedirectAction from "@kiwicom/frontend-mmb/services/mmb2/mmb2RedirectAction"
import { MMB1_MIGRATED_MODALS } from "@kiwicom/frontend-mmb/services/mmb2/types"
import { BookingDetailsViewBookingDetailsUpsellBannersPaidGuarantee as PaidGuaranteeUpsellBannerData } from "@kiwicom/openapi-sdk/mmb"
import useMediaQuery from "@kiwicom/orbit-components/lib/hooks/useMediaQuery"
import { useLog } from "@kiwicom/nitro/lib/services/log/context"
import { createEventLogUnified } from "@kiwicom/frontend-mmb/services/tracking/createEventLogUnified"
import {
  PaidGuaranteeModule,
  UIAction,
} from "@kiwicom/frontend-mmb/services/tracking/constants/trackingEvents"

export const GUARANTEE_CASES = {
  // on MMB we don't want to show any banner for SHOT emergencies
  HIDDEN: "hidden",
  PAID: "paid",
  NOT_PAID: "not_paid",
  META_PARTNER_PAID: "meta_partner_paid",
  META_PARTNER_NOT_PAID: "meta_partner_not_paid",
  LEGACY: "legacy",
  // on Booking, before the user had a change to attach or not attach the guarantee
  NOT_SELECTED_YET: "not_selected_yet",
} as const

export type GuaranteeCase = typeof GUARANTEE_CASES[keyof typeof GUARANTEE_CASES]

type Props = {
  context: ItineraryContext
  providerWarning: boolean
  hasGuarantee: Guarantee
  guaranteeCase: GuaranteeCase
  paidGuaranteeUpsell?: PaidGuaranteeUpsellBannerData | null
}

const ItineraryGuaranteeWrapper = styled.div`
  padding-bottom: ${({ theme }) => theme.orbit.space600};
`

const IconWrapper = styled.span`
  margin-right: ${({ theme }) => theme.orbit.space200};
`

const GUARANTEE_INCLUDED_TITLE = (
  <FormattedMessage
    id="multi_modal.itinerary_detail.layover.guarantee.banner.protected.title"
    defaultMessage="{companyName} Guarantee "
    values={{ companyName: "Kiwi.com" }}
  />
)
const TRANSFER_PROTECTION_INCLUDED_TITLE = (
  <FormattedMessage
    id="multi_modal.itinerary_detail.layover.guarantee.banner.transfer_protection_included.title"
    defaultMessage="Transfer protection included"
  />
)
const GUARANTEE_NOT_INCLUDED_TITLE = (
  <FormattedMessage
    id="multi_modal.itinerary_detail.layover.guarantee.banner.unprotected.title"
    defaultMessage="{companyName} Guarantee is not included"
    values={{ companyName: "Kiwi.com" }}
  />
)

const GUARANTEE_INCLUDED_TEXT = (
  <FormattedMessage
    id="multi_modal.itinerary_detail.layover.guarantee.banner.protected.text"
    defaultMessage="If your trip is affected by a carrier delay or cancellation, we’ll provide you with a <strong>free alternative or instant refund</strong>. <ref>Learn more</ref>"
    values={{
      ref: (ref) => (
        <TextLink href="/pages/guarantee" external rel="noreferrer noopener" type="primary">
          <span>{ref}</span>
        </TextLink>
      ),
    }}
  />
)
const TRANSFER_PROTECTION_INCLUDED_TEXT = (
  <FormattedMessage
    id="multi_modal.itinerary_detail.layover.guarantee.banner.transfer_protection_included.text"
    defaultMessage="We won’t leave you stranded at the airport. If your trip is affected by a carrier delay or cancellation <strong>while at the airport</strong>, we’ll provide you with a free alternative. "
  />
)

// we're dispatching thunk via middleware
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
const guaranteeNotIncludedText = (dispatch: Dispatch<unknown>) => {
  const onClick = (e: { stopPropagation: () => void }) => {
    // since this is within the Itinerary component, we don't want the even to bubble up so the Itinerary component is not closed unexpectedly
    e.stopPropagation()
    dispatch(openHelp(articles.WHAT_IS_GUARANTEE))
  }

  return (
    <FormattedMessage
      id="multi_modal.itinerary_detail.layover.guarantee.banner.unprotected.text"
      defaultMessage="You’ve decided to book this trip without {companyName} Guarantee. If your trip is affected by a carrier delay or cancellation, we’ll provide you <strong>with paid alternatives only</strong>. <ref>Learn more</ref>"
      values={{
        companyName: "Kiwi.com",
        ref: (ref) => (
          <TextLink type="primary" onClick={onClick}>
            {/* TODO MMB-3023: check if span can be removed once refactored to react-intl */}
            <span>{ref}</span>
          </TextLink>
        ),
      }}
    />
  )
}

type LegacyGuaranteeBannerProps = {
  context: ItineraryContext
  providerWarning: boolean
  hasGuarantee: Guarantee
}

export const LegacyGuaranteeBanner = ({
  context,
  providerWarning,
  hasGuarantee,
}: LegacyGuaranteeBannerProps) => {
  if (!hasGuarantee) {
    return null
  }
  return (
    <ItineraryGuaranteeWrapper data-test="ItineraryGuarantee">
      <Separator />
      <Text spaceAfter="small">
        <IconWrapper>
          <KiwicomGuaranteeIcon ariaHidden color="success" />
        </IconWrapper>
        <FormattedMessage
          id="result.guarantee.title"
          defaultMessage="{companyName} Guarantee"
          values={{ companyName: "Kiwi.com" }}
        />
      </Text>
      <Text size="small" type="secondary" spaceAfter="small">
        {context === CONTEXT_TYPES.RESULTS ? (
          <FormattedMessage
            id="result.guarantee.message"
            defaultMessage="Worried about missing your connection? The {companyName} Guarantee has you covered."
            values={{ companyName: "Kiwi.com" }}
          />
        ) : (
          <FormattedMessage
            id="booking.global.guarantee.body"
            defaultMessage="Worried about missing your connection? The {companyName} Guarantee has you covered."
            values={{ companyName: "Kiwi.com" }}
          />
        )}{" "}
        <TextLink href="/pages/guarantee" external rel="noreferrer noopener" type="primary">
          <FormattedMessage id="common.more_info" defaultMessage="Learn more" />
        </TextLink>
      </Text>
      {/* FIXME: remove true */}
      {/* Fix this the next time the file is edited. (Flow to TS) */}
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
      {(providerWarning || true) && (
        <Text type="secondary" size="small">
          <FormattedMessage
            id="result.guarantee.provider_warning"
            defaultMessage="The {companyName} Guarantee only applies to bookings made on {companyName}"
            values={{ companyName: "Kiwi.com" }}
          />
        </Text>
      )}
    </ItineraryGuaranteeWrapper>
  )
}

type PaidGuaranteeBannerProps = {
  hasGuarantee: Guarantee
  context: ItineraryContext
  guaranteeCase: GuaranteeCase
  paidGuaranteeUpsell?: PaidGuaranteeUpsellBannerData | null
}

const getGuaranteeBannerTitleOrText = (
  guaranteeCase: GuaranteeCase,
  isTitle: boolean,
  dispatch: Dispatch,
): React.ReactElement<React.ComponentProps<typeof FormattedMessage>> => {
  switch (guaranteeCase) {
    case GUARANTEE_CASES.PAID:
      return isTitle ? GUARANTEE_INCLUDED_TITLE : GUARANTEE_INCLUDED_TEXT
    case GUARANTEE_CASES.NOT_PAID:
      return isTitle ? GUARANTEE_NOT_INCLUDED_TITLE : guaranteeNotIncludedText(dispatch)
    case GUARANTEE_CASES.META_PARTNER_PAID:
      return isTitle ? GUARANTEE_INCLUDED_TITLE : GUARANTEE_INCLUDED_TEXT
    case GUARANTEE_CASES.META_PARTNER_NOT_PAID:
      return isTitle ? TRANSFER_PROTECTION_INCLUDED_TITLE : TRANSFER_PROTECTION_INCLUDED_TEXT
    default:
      // TODO log
      return isTitle ? GUARANTEE_NOT_INCLUDED_TITLE : guaranteeNotIncludedText(dispatch)
  }
}

/**
 * Returns correct icon used in Guarantee Banner
 *
 * @param guaranteeCase
 * @param hasGuarantee
 * @return {JSX.Element}
 */
export const getGuaranteeIcon = (guaranteeCase: GuaranteeCase, hasGuarantee: Guarantee) => {
  if (guaranteeCase === GUARANTEE_CASES.LEGACY) {
    return hasGuarantee ? (
      <KiwicomGuaranteeIcon ariaHidden color="success" />
    ) : (
      <AlertIcon ariaHidden color="critical" />
    )
  }

  switch (guaranteeCase) {
    case GUARANTEE_CASES.PAID:
    case GUARANTEE_CASES.META_PARTNER_PAID:
    case GUARANTEE_CASES.META_PARTNER_NOT_PAID:
      return <KiwicomGuaranteeIcon ariaHidden color="success" />
    case GUARANTEE_CASES.NOT_PAID:
      return <AlertIcon ariaHidden color="critical" />
    default:
      // returning critical, we don't want to over-promise
      return <AlertIcon ariaHidden color="critical" />
  }
}

const ItineraryPaidGuaranteeUpsellBanner = ({
  paidGuaranteeUpsell,
}: {
  paidGuaranteeUpsell: PaidGuaranteeUpsellBannerData
}) => {
  const dispatch = useDispatch()
  const { isTablet } = useMediaQuery()
  const { log } = useLog()

  useEffect(() => {
    void log(createEventLogUnified(PaidGuaranteeModule, UIAction.bannerDisplayed), {
      variant: "banner",
      reason: "itinerary detail",
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onAddButtonClick = async () => {
    await log(createEventLogUnified(PaidGuaranteeModule, UIAction.buttonClicked, "interested"), {
      variant: "banner",
      reason: "itinerary detail",
    })
    dispatch(mmb2RedirectAction(MMB1_MIGRATED_MODALS.DISRUPTION_PROTECTION_UPSELL))
  }

  return (
    <ItineraryGuaranteeWrapper data-test="ItineraryGuarantee">
      <Separator spaceAfter="medium" />
      <Stack direction="row" align="center" spacing="200" spaceAfter="small">
        <AlertIcon ariaHidden color="critical" />
        <Text weight="bold">
          <FormattedMessage
            id="mmb.itinerary_overview.guarantee_banner.not_included_upsell.title"
            defaultMessage="The {companyName} Guarantee is not included"
            values={{ companyName: "Kiwi.com" }}
            description="Figma: https://www.figma.com/file/Lrol98xBbdJOsozU5ZYC7T/%F0%9F%9B%A1-Guarantee-in-MMB?node-id=7635-109053"
          />
        </Text>
      </Stack>
      <Stack spacing="200">
        <div>
          <Text type="secondary">
            <FormattedMessage
              id="mmb.itinerary_overview.guarantee_banner.not_included_upsell.description_1"
              defaultMessage="If carrier disruptions break your itinerary, you could end up paying for a new ticket."
              description="Figma: https://www.figma.com/file/Lrol98xBbdJOsozU5ZYC7T/%F0%9F%9B%A1-Guarantee-in-MMB?node-id=7635-109053"
            />
          </Text>
          <Text weight="medium">
            <FormattedMessage
              id="mmb.itinerary_overview.guarantee_banner.not_included_upsell.description_2"
              defaultMessage="Add the <ref>{companyName} Guarantee</ref> to get free alternatives or a full refund."
              values={{
                companyName: "Kiwi.com",
                ref: (ref) => (
                  <TextLink
                    onClick={() => {
                      dispatch(openHelp(articles.WHAT_IS_GUARANTEE))
                    }}
                  >
                    {ref}
                  </TextLink>
                ),
              }}
              description="Figma: https://www.figma.com/file/Lrol98xBbdJOsozU5ZYC7T/%F0%9F%9B%A1-Guarantee-in-MMB?node-id=7635-109053"
            />
          </Text>
        </div>
        <Button
          size={isTablet ? "small" : "normal"}
          fullWidth={!isTablet}
          type="primarySubtle"
          onClick={onAddButtonClick}
        >
          <FormattedMessage
            id="mmb.itinerary_overview.guarantee_banner.not_included_upsell.button"
            defaultMessage="Add for {price}"
            values={{
              price: (
                <Price currency={paidGuaranteeUpsell.price.currency.toLowerCase()} alreadyConverted>
                  {paidGuaranteeUpsell.price.amount}
                </Price>
              ),
            }}
            description="Figma: https://www.figma.com/file/Lrol98xBbdJOsozU5ZYC7T/%F0%9F%9B%A1-Guarantee-in-MMB?node-id=7635-109053"
          />
        </Button>
      </Stack>
    </ItineraryGuaranteeWrapper>
  )
}

export const PaidGuaranteeBanner = ({
  hasGuarantee,
  context,
  guaranteeCase,
  paidGuaranteeUpsell,
}: PaidGuaranteeBannerProps) => {
  const dispatch = useDispatch()

  if (paidGuaranteeUpsell && context === CONTEXT_TYPES.MMB) {
    return <ItineraryPaidGuaranteeUpsellBanner paidGuaranteeUpsell={paidGuaranteeUpsell} />
  }

  /**
   * The banner doesn't display on the Booking page.
   */
  if (
    [
      CONTEXT_TYPES.RESERVATION,
      CONTEXT_TYPES.RESERVATION_ALTERNATIVES,
      CONTEXT_TYPES.RESERVATION_SUMMARY,
      // @ts-expect-error: Flow to TS migration.
    ].includes(context)
  ) {
    return null
  }

  const titleMessage = getGuaranteeBannerTitleOrText(guaranteeCase, true, dispatch)
  const textMessage = getGuaranteeBannerTitleOrText(guaranteeCase, false, dispatch)

  return (
    <ItineraryGuaranteeWrapper data-test="ItineraryGuarantee">
      <Separator spaceAfter="medium" />
      <Text spaceAfter="small">
        <IconWrapper>{getGuaranteeIcon(guaranteeCase, hasGuarantee)}</IconWrapper>
        {titleMessage}
      </Text>
      <Text size="small" type="secondary" spaceAfter="small">
        {textMessage}
      </Text>
    </ItineraryGuaranteeWrapper>
  )
}

const ItineraryGuarantee = ({
  context,
  providerWarning,
  hasGuarantee,
  guaranteeCase,
  paidGuaranteeUpsell,
}: Props) => {
  if (guaranteeCase === GUARANTEE_CASES.HIDDEN && !paidGuaranteeUpsell) {
    return null
  }

  if (guaranteeCase !== GUARANTEE_CASES.LEGACY) {
    return (
      <PaidGuaranteeBanner
        hasGuarantee={hasGuarantee}
        context={context}
        guaranteeCase={guaranteeCase}
        paidGuaranteeUpsell={paidGuaranteeUpsell}
      />
    )
  }

  return (
    <LegacyGuaranteeBanner
      context={context}
      providerWarning={providerWarning}
      hasGuarantee={hasGuarantee}
    />
  )
}

export default ItineraryGuarantee
